<style lang="scss" scoped>
@use 'sass:color';
@use '../../palette.scss' as *;

.unit-list-page {
    display: flex;
    flex-flow: row wrap;
}

.units-list {
    display: flex;
    flex-direction: column;

    .none-unprovisioned {
        font-weight: bold;
        color: $theme-dark-grey;
    }

    .card-container {
        position: relative;
        display: flex;
        align-items: center;

        &:hover,
        &.swiped {
            .unit-card,
            .provision-card {
                margin-right: 55px;
            }
        }

        .provision-card {
            display: flex;
            flex-direction: column;
            color: $theme-dark-grey;
            border-radius: 5px;
            background-color: white;
            padding: 15px;
            box-shadow: $subtle-shadow;
            border: 1px solid $theme-light-grey;
            margin-bottom: 10px;
            width: 100%;
            transition: margin 0.25s;
            z-index: 5;

            .name {
                font-weight: bold;
                font-size: 1.3em;
            }

            .button-block {
                margin-top: 15px;

                button {
                    max-width: 200px;
                }
            }
        }

        .delete-btn-ctn {
            position: absolute;
            width: 55px;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            top: 0;
            right: 0;
            z-index: 0;

            button {
                margin: 0 5px 10px 0;
            }

            button {
                background-color: transparent;
                color: $theme-red;
                font-size: 1.7em;
                border: none;
                outline: none;

                i {
                    border-radius: 50%;

                    &:hover {
                        background-color: color.adjust(
                            $theme-red,
                            $alpha: -0.85
                        );
                        color: color.adjust($theme-red, $lightness: -5%);
                    }

                    &:active {
                        transform: scale(0.97);
                    }
                }
            }
        }

        .unit-card {
            display: flex;
            flex-direction: column;
            color: $theme-dark-grey;
            border-radius: 5px;
            background-color: white;
            padding: 15px;
            box-shadow: $subtle-shadow;
            border: 1px solid $theme-light-grey;
            margin-bottom: 10px;
            width: 100%;
            transition: margin 0.25s;
            z-index: 5;

            &:hover {
                background-color: color.adjust(white, $lightness: -5%);
                cursor: pointer;
            }

            &:active {
                transform: scale(0.99);
            }

            &.spaced {
                margin-bottom: 10px;
            }

            .name-block {
                display: flex;
                align-items: baseline;

                .name {
                    font-weight: bold;
                    font-size: 1.3em;
                }

                .id {
                    font-size: 0.8em;
                    font-weight: bold;
                    font-style: italic;
                    color: $theme-light-grey;
                    margin-left: 10px;
                }
            }
        }
    }
}

.prop-block {
    display: flex;
    flex-flow: column;

    .property {
        display: flex;
        align-items: baseline;
        padding: 10px;
        width: 100%;
        word-break: break-word;

        .property-name {
            font-weight: bold;
            margin-right: 8px;
        }
    }

    .input-box-container {
        width: 60%;
        margin-left: 10px;
    }
}

@media (min-width: 992px) {
    .units-list .unit-card .prop-block {
        flex-flow: row nowrap;
        justify-content: space-between;

        .property {
            width: 47%;
        }
    }
}

::v-deep(.modal-footer button) {
    margin-right: 10px;
}

::v-deep(.modal-footer button:last-child) {
    margin-right: 0;
}
</style>

<template>
    <div class="unit-list-page">
        <Card color="blue" width="550px" :maxWidth="'1200px'">
            <template #header>{{ $t('unit_plural') }}</template>
            <template #body>
                <div class="units-list">
                    <v-touch
                        v-for="(unit, index) in provisioned"
                        :key="unit.id"
                        v-touch:swipe.left="() => (swipeIndex = index)"
                        v-touch:swipe.right="() => swipeUnitRight(index)"
                        class="card-container"
                        :class="{ swiped: swipeIndex === index }"
                    >
                        <div
                            class="unit-card"
                            :class="{ spaced: index !== units.length - 1 }"
                            @click="selectUnit(unit)"
                        >
                            <div class="name-block">
                                <span class="name">{{ unit.name }}</span>
                                <span class="id">id: {{ unit.id }}</span>
                            </div>
                            <div class="prop-block">
                                <div class="property">
                                    <span class="property-name"
                                        >{{ $t('iridiumImei') }}:</span
                                    >
                                    <span>{{ unit.iridium_imei }}</span>
                                </div>
                                <div class="property">
                                    <span class="property-name"
                                        >{{ $t('lastUpdateTime') }}:</span
                                    >
                                    <span>{{ unitAges[unit.id] }}</span>
                                </div>
                            </div>
                            <div class="prop-block" v-if="unit.campaign_id">
                                <div class="property">
                                    <span class="property-name"
                                        >{{ $t('activeIn') }}:
                                    </span>
                                    <span>{{ unit.campaign_name }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="delete-btn-ctn">
                            <button @click="deleteUnit(unit)">
                                <i class="far fa-times-circle" />
                            </button>
                        </div>
                    </v-touch>
                </div>
            </template>
        </Card>
        <Card color="blue" width="550px" :maxWidth="'1200px'">
            <template #header>{{ $t('provisioning') }}</template>
            <template #body>
                <div class="units-list">
                    <div v-if="checkUnprovisioned" class="none-unprovisioned">
                        {{ $t('noUnprovisionedUnits') }}
                    </div>
                    <v-touch
                        v-for="(unit, index) in unprovisioned"
                        :key="unit.id"
                        v-touch:swipe.left="
                            () => (provisioningSwipeIndex = index)
                        "
                        v-touch:swipe.right="
                            () => swipeProvisioningRight(index)
                        "
                        class="card-container"
                        :class="{ swiped: provisioningSwipeIndex === index }"
                    >
                        <div class="provision-card">
                            <span class="name">{{ unit.unit_serial }}</span>
                            <div class="prop-block">
                                <div class="property">
                                    <span class="property-name">{{
                                        $t('name')
                                    }}</span>
                                    <InputBox
                                        type="text"
                                        :placeholder="$t('noPreviousName')"
                                        @keydown.enter="provisionUnit(unit)"
                                        v-model="provisioningNames[unit.id]"
                                    />
                                </div>
                                <div class="property">
                                    <span class="property-name"
                                        >{{ $t('lastUpdateTime') }}:</span
                                    >
                                    <span>{{ unitAges[unit.id] }}</span>
                                </div>
                            </div>
                            <div class="button-block">
                                <Button
                                    :disabled="!provisioningNames[unit.id]"
                                    @click="provisionUnit(unit)"
                                    >{{ $t('provision') }}</Button
                                >
                            </div>
                        </div>
                        <div class="delete-btn-ctn">
                            <button @click="deleteUnit(unit)">
                                <i class="far fa-times-circle"></i>
                            </button>
                        </div>
                    </v-touch>
                </div>
            </template>
        </Card>

        <!-- Delete verification modal -->
        <Modal
            v-if="showDeleteVerify"
            @ok="doUnitDelete()"
            @cancel="showDeleteVerify = false"
        >
            <template #header>
                <h3>{{ $t('verifyDelete') }}</h3>
            </template>
            <template #body>
                <div class="form-group">
                    {{
                        $t('verifyUnitDelete', {
                            unit_name: unitToBeDeleted.name
                                ? unitToBeDeleted.name
                                : unitToBeDeleted.unit_serial,
                        })
                    }}
                </div>
            </template>
        </Modal>
    </div>
</template>

<script>
import toastr from 'toastr';

import {
    getUnitsList,
    getUnfilteredUnitsList,
    deleteUnit,
    provision,
    getUnit,
    setOrganizations,
} from '../../api/units';
import { getOrganizationUser, getOrganizations } from '../../api/organizations';
import { changeMonitor } from '../../api/ChangeMonitor';
import { STATUS_OK, STATUS_ERROR } from '../../api/client';
import { getAgeString } from '../../util/util';

export default {
    data() {
        return {
            showDeleteVerify: false,
            unitToBeDeleted: undefined,
            units: [],
            unprovisioned: [],
            provisioningNames: {},
            syncInFlight: null,
            syncPending: false,
            swipeIndex: -1,
            provisioningSwipeIndex: -1,
            unitAges: {},
            ageLoop: undefined,
            finishedInitialLoad: false,
        };
    },

    computed: {
        organizations() {
            return this.$store.state.organizations;
        },

        currentUser() {
            return this.$store.state.currentUser;
        },

        unitList() {
            return this.units.sort(this.$store.getters.intlCompareByName);
        },

        provisioned() {
            return this.unitList.filter((u) => u.provisioned);
        },

        checkUnprovisioned() {
            return this.unprovisioned.length === 0 && this.finishedInitialLoad;
        },
    },

    created: async function () {
        await this.syncUnits();
        changeMonitor.addNotifiedUser(this.handleChanges);
        this.updateUnitAges();
        this.finishedInitialLoad = true;

        this.ageLoop = setTimeout(this.updateUnitAges, 1000);
    },

    beforeDestroy() {
        if (this.ageLoop) {
            clearTimeout(this.ageLoop);
            this.ageLoop = undefined;
        }

        changeMonitor.removeNotifiedUser(this.handleChanges);
    },

    methods: {
        async ensureOrganizationsLoaded() {
            if (
                Array.isArray(this.organizations) &&
                this.organizations.length > 0
            ) {
                return this.organizations;
            }

            const response = await getOrganizations();
            if (
                response?.status === STATUS_OK &&
                Array.isArray(response.payload)
            ) {
                this.$store.commit('setOrganizations', response.payload);
                return response.payload;
            }

            return [];
        },

        normalizeOrganizationName(name) {
            return String(name || '')
                .replace(/["']/g, '')
                .trim()
                .toLowerCase();
        },

        // recursive method to find all children and children of children
        async addMyChildOrganizations(myChildOrgs, orgs, parentsId) {
            for (const organization of orgs) {
                if (organization.parent_organization_id === parentsId) {
                    myChildOrgs.push({
                        organizationId: organization.id,
                        organizationName: organization.organization_name,
                        organizationParentId:
                            organization.parent_organization_id,
                        twoFa: organization.two_factor,
                    });
                    await this.addMyChildOrganizations(
                        myChildOrgs,
                        orgs,
                        organization.id,
                    );
                }
            }
            return myChildOrgs;
        },

        async getUnprovisionedUnits(units) {
            let unprovisioned = [];

            await this.ensureOrganizationsLoaded();

            // if no organizations or current user return nothing
            if (!this.organizations || !this.currentUser) {
                return [];
            }

            // if user is server admin return whole unprovisioned unit list
            if (this.currentUser.role === 'server_admin') {
                return units.filter((unit) => !unit.provisioned);
            }

            // still need filter by unprovisioned
            let unprovisionedUnits = units.filter((unit) => !unit.provisioned);

            let myOrgs = [];
            let myOrg = null;
            // get the list of organizations by user
            const result = await getOrganizationUser(this.currentUser.id);
            if (result.status === STATUS_OK) {
                myOrg = result.payload;
            }
            // if the user isn't in an organization return nothing
            if (!myOrg || !myOrg.organization_id) {
                return [];
            }
            // add the users organization to the organizations supported list by default
            myOrgs.push({
                organizationId: myOrg.organization_id,
                organizationName: this.organizations.find(
                    (org) => org.id === myOrg.organization_id,
                )?.organization_name,
                organizationParentId: this.organizations.find(
                    (org) => org.id === myOrg.organization_id,
                )?.parent_organization_id,
                twoFa: this.organizations.find(
                    (org) => org.id === myOrg.organization_id,
                )?.two_factor,
            });
            // recursively find all child organizations of the user's organization and add them to
            // the list of supported organizations for the user
            myOrgs = await this.addMyChildOrganizations(
                myOrgs,
                this.organizations,
                myOrg.organization_id,
            );

            // filter through unprovisioned units that are either in the user's org
            for (const unit of unprovisionedUnits) {
                // first check if can_config has an org assignment for the unit and use that and ignore
                // the unit org settings
                let canConfigOrg;
                if (unit.can_config) {
                    canConfigOrg = this.normalizeOrganizationName(
                        unit.can_config.org,
                    );
                    if (
                        myOrgs.some(
                            (org) =>
                                this.normalizeOrganizationName(
                                    org.organizationName,
                                ) === canConfigOrg,
                        )
                    ) {
                        unprovisioned.push(unit);
                        continue;
                    }
                }

                // if no unprovisioned added yet then check to list unprovisioned controller based
                // on unit orgs that match the user's orgs
                const fullUnitInfoResponse = await getUnit(unit.id).then(
                    (res) => {
                        if (res.status === STATUS_OK) {
                            return res;
                        }
                        // if the unit is not found it may be because the unit is only in the unfiltered list and not the provisioned list
                        //  so we can ignore not found errors here and just return null to be handled below
                        else if (
                            res.status === STATUS_ERROR &&
                            res.msg === 'unitNotFound'
                        ) {
                            return null;
                        }
                        // for any other error we want to log it so we can investigate but still return null so the unit doesn't get added to the unprovisioned list
                        else if (res.status === STATUS_ERROR) {
                            console.error(
                                'Error fetching full unit info for unit ',
                                unit.id,
                                ': ',
                                res,
                            );
                            return null;
                        }
                    },
                );
                if (
                    fullUnitInfoResponse &&
                    fullUnitInfoResponse.status === STATUS_OK
                ) {
                    const unitInfo = fullUnitInfoResponse.payload;
                    const unitOrgs = Array.isArray(unitInfo.organizations)
                        ? unitInfo.organizations
                        : [];

                    if (
                        unitOrgs.some((org) =>
                            myOrgs.some(
                                (myOrg) =>
                                    Number(myOrg.organizationId) ===
                                    Number(org.organization_id),
                            ),
                        )
                    ) {
                        unprovisioned.push(unit);
                    }
                } else if (fullUnitInfoResponse === null) {
                    // Ignore not found errors as they are not relevent for provisioning for currentUser and organization.
                    continue;
                }
            }

            return unprovisioned;
        },

        selectUnit(unit) {
            this.$router.push({ path: `/admin/units/${unit.id}` });
        },

        deleteUnit(unit) {
            this.unitToBeDeleted = unit;
            this.showDeleteVerify = true;
        },

        async doUnitDelete() {
            this.showDeleteVerify = false;

            if (!this.unitToBeDeleted) {
                console.warn('No unit to be deleted');
                return;
            }

            const response = await deleteUnit(this.unitToBeDeleted.id);
            if (response.status === STATUS_OK) {
                toastr.success(this.$t('unitDeleteSuccessful'));
            } else if (response.status === STATUS_ERROR) {
                if (
                    response.db_message.includes(
                        'Unable to delete unit in an active campaign',
                    )
                ) {
                    toastr.error(
                        this.$t('problemDeletingUnit') +
                            ' : ' +
                            this.$t('campaignEnabled'),
                    );
                } else {
                    toastr.error(this.$t('problemDeletingUnit'));
                }
            }
        },

        async handleChanges(change) {
            if (change.changeType === 'UNIT') {
                // Set provisioning in either case
                if (change.data.provisioning) {
                    change.data.provisioned = true;
                } else {
                    change.data.provisioned = false;
                }
                // refresh the page with latest database data
                await this.syncUnits();
            }
        },

        // Collapse overlapping sync requests: if one is in flight, queue a single
        //  follow-up run instead of starting a parallel one that could finish out of order.
        async syncUnits() {
            if (this.syncInFlight) {
                this.syncPending = true;
                return this.syncInFlight;
            }

            this.syncInFlight = (async () => {
                do {
                    this.syncPending = false;
                    await this.doSyncUnits();
                } while (this.syncPending);
            })();

            try {
                await this.syncInFlight;
            } finally {
                this.syncInFlight = null;
            }
        },

        async doSyncUnits() {
            if (this.$store.state.authToken == null) return;
            // get list of units from database and set to local state
            const unitsResponse = await getUnitsList();
            if (unitsResponse && unitsResponse.payload) {
                this.units = unitsResponse.payload;
            }

            // get list of all units regardless of provisioning status to use
            //     for provisioning panel and filtering
            const unfilteredUnitsResponse = await getUnfilteredUnitsList();

            const units = Array.isArray(unfilteredUnitsResponse)
                ? unfilteredUnitsResponse
                : unfilteredUnitsResponse?.payload;

            if (Array.isArray(units)) {
                this.unprovisioned = await this.getUnprovisionedUnits(units);

                // For every unit that is not provisioned, create a place in our data model
                //  for storing mutable unit names for the provisioning panel. Only seed
                //  units we haven't seen yet so a sync doesn't overwrite in-progress typing.
                const unprovisionedIds = new Set(
                    this.unprovisioned.map((u) => String(u.id)),
                );
                this.unprovisioned.forEach((u) => {
                    if (!(u.id in this.provisioningNames)) {
                        this.provisioningNames[u.id] = u.name || '';
                    }
                });
                // Drop names for units that are no longer awaiting provisioning
                for (const id of Object.keys(this.provisioningNames)) {
                    if (!unprovisionedIds.has(id)) {
                        delete this.provisioningNames[id];
                    }
                }
            } else if (!this.units) {
                this.units = [];
            }
        },

        async provisionUnit(unit) {
            const copy = { ...unit };
            copy.name = this.provisioningNames[unit.id];

            const response = await provision(unit.id, copy);

            if (response.status !== 'OK') {
                console.error('problem provisioning: ', response);
            }

            const provisionedUnit = response.payload;

            // copy a map of the organizations from provisioned unit
            let unitOrgs = provisionedUnit.organizations;
            if (!Array.isArray(unitOrgs)) {
                unitOrgs = [];
            }

            const organizations = await this.ensureOrganizationsLoaded();

            // add the new can_config org if it does not already exists in the list
            // of organizations for the unit
            if (
                unit.can_config?.org?.trim() &&
                organizations &&
                Array.isArray(unitOrgs)
            ) {
                const canConfigOrg = this.normalizeOrganizationName(
                    unit.can_config.org,
                );

                const foundOrg = organizations.find(
                    (org) =>
                        this.normalizeOrganizationName(
                            org.organization_name,
                        ) === canConfigOrg,
                );

                if (foundOrg?.id != null) {
                    const orgId = parseInt(foundOrg.id);
                    if (!isNaN(orgId)) {
                        unitOrgs.push({
                            organization_id: orgId,
                            name: unit.can_config.org.replace(/"/g, "'"),
                        });
                    }
                }
            }

            // update the unit organizations with the new list
            const orgSetResponse = await setOrganizations(
                provisionedUnit.id,
                unitOrgs,
            );
            // if error add issue to process error list
            if (orgSetResponse.status !== 'OK') {
                // need translations
                toastr.error('Unit Provisioned but Organization Update Error');
            }
        },

        // Responsible for hiding the delete button for a particular unit if it's
        //  swiped closed
        swipeUnitRight(index) {
            if (index === this.swipeIndex) {
                this.swipeIndex = -1; // "Unswipe" the current unit
            }
            // If the id doesn't match, swiping right doesn't do anything
        },

        // Responsible for hiding the delete button fro a particular unprovisioned unit
        //   if it's swiped closed
        swipeProvisioningRight(index) {
            if (index === this.provisioningSwipeIndex) {
                this.provisioningSwipeIndex = -1;
            }
        },

        // Runs every second to keep the age strings on the page for the units updating regularly
        updateUnitAges() {
            for (const unit of this.unitList) {
                if (unit.timestamp) {
                    const unitAgeDiff =
                        new Date().valueOf() -
                        new Date(unit.timestamp).valueOf();
                    // We have to use set because we are adding a new property that we want Vue
                    //  to be watching to an object.  If these properties were already there
                    //  this wouldn't be necessary
                    this.unitAges[unit.id] = getAgeString(unitAgeDiff, true);
                } else {
                    // See above
                    this.unitAges[unit.id] = this.$t('na');
                }
            }

            if (this.ageLoop) {
                this.ageLoop = setTimeout(this.updateUnitAges, 1000);
            }
        },
    },
};
</script>
